import i18n from 'i18next';
import type { TOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { env } from '@/config/env';
import { refineResourcesI18nKey } from '@/utils/refineResourceLabel';

/** Refine `useMenu` calls `translate(\`${name}.${name}\`, friendlyDefault)` — map to our `resources.<leaf>` keys. */
const REFINE_DOUBLED_RESOURCE_KEY = /^(.+)\.\1$/;

export function mapRefineMenuResourceKey(key: string): string | undefined {
  const m = REFINE_DOUBLED_RESOURCE_KEY.exec(key);
  const name = m?.[1];
  if (!name) return undefined;
  return refineResourcesI18nKey(name);
}

function buildTOptions(
  options?: unknown,
  defaultMessage?: string
): TOptions | undefined {
  const fromDefault =
    typeof defaultMessage === 'string'
      ? { defaultValue: defaultMessage }
      : undefined;
  if (options === undefined || options === null) {
    return fromDefault;
  }
  if (typeof options === 'string') {
    return { ...fromDefault, defaultValue: options };
  }
  if (typeof options === 'object' && !Array.isArray(options)) {
    return fromDefault
      ? { ...(options as object), ...fromDefault }
      : (options as TOptions);
  }
  return fromDefault;
}

/** Resolves when i18n finished initial load (needed before Refine `useMenu` builds labels). */
export const i18nInitPromise = i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: env.AVAILABLE_LOCALES,
    fallbackLng: env.DEFAULT_LOCALE,
    lng: env.DEFAULT_LOCALE,
    debug: env.ENABLE_DEBUG,

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: true,
    },

    ns: ['common'],
    defaultNS: 'common',
  });

export default i18n;

// Refine i18n provider
export const i18nProvider = {
  translate: (key: string, options?: unknown, defaultMessage?: string) => {
    const mappedKey = mapRefineMenuResourceKey(key);
    const lookupKey = mappedKey ?? key;
    const tOpts = buildTOptions(options, defaultMessage);
    // i18next 25.10 overloads reject a non-literal key + TOptions; use a narrowed signature.
    const t = i18n.t as (key: string, options?: TOptions) => string;
    let result = String(t(lookupKey, tOpts));
    if (mappedKey && result === mappedKey) {
      result = String(t(key, tOpts));
    }
    return result;
  },
  changeLocale: (lang: string) => i18n.changeLanguage(lang),
  getLocale: () => i18n.language,
};
